import {
  CostConfigApi,
  DistributionType,
  SessionDetailsApi,
  SessionItemType,
  type AttendeeRowResponse,
  type CostConfigResponse,
  type FinancingModeRef,
  type GetItemAvailableFinancingModesItemTypeEnum,
  type ItemFinancingModesResponse,
  type PerModeAmountDto,
  type PerModeAmountResponse,
  type SelectItemFinancingModesItemTypeEnum,
} from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useFormats } from "~/composables/utils/useFormat";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { MAX_SESSION_FINANCING_MODES } from "~/utils/enums/financing-modes.enums";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import type { IFindOptions } from "~/utils/interfaces/table-interfaces";
import type { IFinancingMode } from "~/utils/interfaces/financing-modes-interfaces";
import type {
  FinancingAmounts,
  ICostAttendeeRow,
  ISessionCostsStore,
  ISessionFinancingMode,
} from "~/utils/interfaces/session-costs-interfaces";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";

// The cost matrix columns + the "Gérer les modes" picker are sourced from
// CostConfigApi.getItemAvailableFinancingModes (FinancingModeRef carries the real
// financingModeId / code / name / isSystem). Amounts stay keyed by financingModeId.
const toSessionMode = (ref: FinancingModeRef): ISessionFinancingMode => ({
  financingModeId: ref.financingModeId,
  name: ref.name,
  code: ref.code,
  isSystem: ref.isSystem,
});

const toFinancingMode = (ref: FinancingModeRef): IFinancingMode => ({
  id: ref.financingModeId,
  name: ref.name,
  code: ref.code,
  description: "",
  isDefault: false,
  isActive: ref.active,
  isSystem: ref.isSystem,
});

// A side is "empty" (unconfigured) when it has no keys or every amount is zero — used to gate
// convert-on-swap so server-provided / manually-entered amounts are never overwritten.
const isAmountsEmpty = (amounts: FinancingAmounts): boolean => Object.values(amounts).every((v) => !v);

const sumAmounts = (amounts: FinancingAmounts): number => Object.values(amounts).reduce((sum, v) => sum + v, 0);

const cloneAmounts = (amounts: FinancingAmounts): FinancingAmounts => ({ ...amounts });

const cloneRows = (rows: ICostAttendeeRow[]): ICostAttendeeRow[] =>
  rows.map((r) => ({ ...r, amounts: cloneAmounts(r.amounts) }));

// Per-mode amounts keyed by financing-mode id — preserve exactly what the server returned.
// Amounts are decoupled from the column set: a mode without a column keeps its amount, and a
// column without an amount simply reads 0 at render (CostsTable uses `amounts[id] ?? 0`).
const amountsFromResponse = (amounts: PerModeAmountResponse[]): FinancingAmounts =>
  Object.fromEntries(amounts.map((a) => [a.financingModeId, a.priceExclTax]));

const amountsToDto = (amounts: FinancingAmounts): PerModeAmountDto[] =>
  Object.entries(amounts).map(([financingModeId, priceExclTax]) => ({ financingModeId, priceExclTax }));

// Sum attendee rows into a per-mode total over every mode present on the rows (not just the
// columns), so a maintained amount outside the column set still counts toward the total.
const sumRowsByMode = (rows: ICostAttendeeRow[]): FinancingAmounts => {
  const acc: FinancingAmounts = {};
  rows.forEach((r) => {
    Object.entries(r.amounts).forEach(([id, value]) => {
      acc[id] = (acc[id] ?? 0) + value;
    });
  });
  return acc;
};

// Full-population per-mode total: the server session total plus the loaded page's unsaved delta
// (the server total already counts the page's pre-edit values). Shared by the pedagogical
// indicator and the Participant -> Session swap aggregation.
const layeredAttendeeTotal = (
  server: FinancingAmounts,
  live: FinancingAmounts,
  base: FinancingAmounts,
): FinancingAmounts => {
  const ids = new Set([...Object.keys(server), ...Object.keys(live), ...Object.keys(base)]);
  return Object.fromEntries([...ids].map((id) => [id, (server[id] ?? 0) + ((live[id] ?? 0) - (base[id] ?? 0))]));
};

const toAttendeeRow = (a: AttendeeRowResponse): ICostAttendeeRow => {
  const name = a.displayValue?.trim() ?? "";
  const parts = name ? name.split(/\s+/).filter(Boolean) : [];
  return {
    attendeeId: a.attendeeId,
    fullName: name || "—",
    firstname: parts[0] ?? null,
    lastname: parts.length > 1 ? parts[parts.length - 1] : null,
    entityName: a.legalEntityName?.trim() || null,
    amounts: amountsFromResponse(a.amounts),
  };
};

export const useSessionCostsStore = defineStore("sessionCostsStore", {
  state: (): ISessionCostsStore => ({
    isLoading: false,
    isSaving: false,
    isSavingDefaultPrice: false,
    isApplyingDefaultPrice: false,
    config: {
      mode: SessionItemType.Group,
      distribution: DistributionType.Session,
      isInternalSession: false,
    },
    sessionModes: [],
    availableModes: [],
    uniformAmounts: {},
    attendees: [],
    attendeesPagination: { total: 0, offset: 0, limit: 50 },
    indicators: {
      pedagogicalTotal: 0,
      pedagogicalBreakdown: {},
      additionalChargesTotal: null,
      salaryAttendees: null,
      salaryTrainers: null,
    },
    catalog: { groupCatalogPrice: null, individualCatalogPrice: null },
    defaultAttendeePrice: null,
    isEditMode: false,
    snapshot: null,
  }),

  getters: {
    sessionTag(state): { label: "INTRA" | "INTER" | "INTERNE"; className: string } {
      if (state.config.isInternalSession) return { label: "INTERNE", className: "tag_light_green" };
      if (state.config.mode === SessionItemType.Group) return { label: "INTRA", className: "tag_light_purple" };
      return { label: "INTER", className: "tag_light_blue" };
    },

    catalogReference(state): number | null {
      if (state.config.mode === SessionItemType.Group) return state.catalog.groupCatalogPrice;
      // Individual: extrapolate to total so the banner compares apples-to-apples.
      const indi = state.catalog.individualCatalogPrice;
      if (indi === null) return null;
      const n = state.attendeesPagination.total || state.attendees.length;
      return n > 0 ? indi * n : null;
    },

    catalogCurrentValue(state): number | null {
      const total = state.indicators.pedagogicalTotal;
      return total === 0 ? null : total;
    },

    isCatalogBannerVisible(): boolean {
      const ref = this.catalogReference;
      const current = this.catalogCurrentValue;
      if (ref === null || current === null) return false;
      return Math.abs(ref - current) >= 0.01;
    },

    // While editing, reflect in-progress prices live; outside edit mode it is the
    // server-side per-mode SUM (from sessionAmounts.amounts). cancelEdit() restores
    // the amounts and flips isEditMode off, so the card reverts automatically.
    displayedPedagogicalBreakdown(state): FinancingAmounts {
      if (!state.isEditMode || !state.snapshot) return state.indicators.pedagogicalBreakdown;

      // By session: the single "Coût session" row is the whole pedagogical cost.
      if (state.config.distribution === DistributionType.Session) return cloneAmounts(state.uniformAmounts);

      // By attendee: only the current page is editable, so layer the edited page's
      // delta on top of the full server SUM — other pages stay untouched.
      const live = sumRowsByMode(state.attendees);
      const base = sumRowsByMode(state.snapshot.attendees);
      return layeredAttendeeTotal(state.indicators.pedagogicalBreakdown, live, base);
    },

    displayedPedagogicalTotal(): number {
      return sumAmounts(this.displayedPedagogicalBreakdown);
    },
  },

  actions: {
    async fetchCosts(options?: Partial<IFindOptions>) {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails, sessionTraining } = storeToRefs(sessionDetailsStore);
      if (!sessionDetails.value?.id) return;

      const feedback = useFeedback();
      this.isLoading = true;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CostConfigApi);
        const [modesData, data] = await Promise.all([
          apiInstance.getItemAvailableFinancingModes({
            itemId: sessionDetails.value.id,
            // The cost tab only loads sessions/projects, which is exactly what this endpoint accepts.
            itemType: sessionDetails.value.itemType as GetItemAvailableFinancingModesItemTypeEnum,
          }),
          apiInstance.getCostConfig({
            itemId: sessionDetails.value.id,
            itemType: sessionDetails.value.itemType,
            offset: options?.offset ?? this.attendeesPagination.offset,
            limit: options?.limit ?? this.attendeesPagination.limit,
            sortingColumn: options?.sortingColumn ?? undefined,
            order: options?.order ?? undefined,
            search: options?.search ?? undefined,
          }),
        ]);

        this.setItemFinancingModes(modesData);
        this.applyCostConfigResponse(data);

        this.catalog = {
          groupCatalogPrice: sessionTraining.value?.groupPriceExclTax ?? null,
          individualCatalogPrice: sessionTraining.value?.individualPriceExclTax ?? null,
        };

        await this.refreshIndicators();
      } catch (error) {
        console.error("Erreur lors de la récupération des coûts:", error);
        feedback.error(`Une erreur est survenue lors de la récupération des coûts`, "small");
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /** Set the enabled mode columns + the selectable list from the item modes endpoint. */
    setItemFinancingModes(data: ItemFinancingModesResponse) {
      this.sessionModes = data.enabledFinancingModes.map(toSessionMode);
      this.availableModes = data.availableFinancingModes.map(toFinancingMode);
    },

    applyCostConfigResponse(data: CostConfigResponse) {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);

      const isIndividual = data.sessionItemType === SessionItemType.Individual;
      const isInternal = isIndividual && sessionDetails.value?.trainingType === TrainingTypeEnum.INTERNAL;

      this.config = {
        mode: data.sessionItemType ?? SessionItemType.Group,
        distribution: data.distributionType,
        isInternalSession: isInternal,
      };

      // Columns come from setItemFinancingModes (getItemAvailableFinancingModes); amounts are
      // kept exactly as the server returned them (decoupled from the column set).
      const sessionAmounts = amountsFromResponse(data.sessionAmounts.amounts);
      this.uniformAmounts = sessionAmounts;

      this.attendees = data.attendees.items.map(toAttendeeRow);
      this.attendeesPagination = {
        total: data.attendees.total,
        offset: data.attendees.offset,
        limit: data.attendees.limit,
      };

      // defaultAttendeePrice is null under by_session; present (possibly empty) under by_attendee.
      this.defaultAttendeePrice = data.defaultAttendeePrice ? amountsFromResponse(data.defaultAttendeePrice) : null;

      // Pedagogical indicator = server-aggregated per-mode session total (all modes),
      // derived here instead of from getTrainingPlanCostsBreakdown (which only knows
      // budget/opco/cpf).
      this.indicators.pedagogicalBreakdown = cloneAmounts(sessionAmounts);
      this.indicators.pedagogicalTotal = sumAmounts(sessionAmounts);
    },

    async refreshIndicators() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      if (!sessionDetails.value?.id) return;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
        const data = await apiInstance.getTrainingPlanCostsBreakdown({
          itemId: sessionDetails.value.id,
          type: sessionDetails.value.itemType,
        });

        // null when the role lacks RBAC access → propagate so the indicator renders "—".
        // The pedagogical breakdown/total are NOT taken from here anymore (this endpoint
        // only knows budget/opco/cpf); they are derived in applyCostConfigResponse.
        this.indicators.additionalChargesTotal = data.otherCosts;
        this.indicators.salaryAttendees = data.attendeesSalaryCost;
        this.indicators.salaryTrainers = data.trainersSalaryCost;
      } catch (error) {
        console.error("Erreur lors du chargement des indicateurs:", error);
      }
    },

    enterEditMode() {
      this.snapshot = {
        config: { ...this.config },
        sessionModes: this.sessionModes.map((m) => ({ ...m })),
        uniformAmounts: cloneAmounts(this.uniformAmounts),
        attendees: cloneRows(this.attendees),
      };
      this.isEditMode = true;
    },

    cancelEdit() {
      if (!this.snapshot) {
        this.isEditMode = false;
        return;
      }
      this.config = { ...this.snapshot.config };
      this.sessionModes = this.snapshot.sessionModes.map((m) => ({ ...m }));
      this.uniformAmounts = cloneAmounts(this.snapshot.uniformAmounts);
      this.attendees = cloneRows(this.snapshot.attendees);
      this.snapshot = null;
      this.isEditMode = false;
    },

    async saveEdit() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      if (!sessionDetails.value?.id) return;

      const feedback = useFeedback();
      this.isSaving = true;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CostConfigApi);

        // Persist the column selection (modes added/removed via "Gérer les modes") before
        // writing amounts, so the cost-config update targets the right set of modes.
        const modesData = await apiInstance.selectItemFinancingModes({
          itemId: sessionDetails.value.id,
          itemType: sessionDetails.value.itemType as SelectItemFinancingModesItemTypeEnum,
          selectFinancingModesDto: { financingModeIds: this.sessionModes.map((m) => m.financingModeId) },
        });
        this.setItemFinancingModes(modesData);

        let data: CostConfigResponse;

        if (this.config.distribution === DistributionType.Session) {
          data = await apiInstance.updateCostConfigPriceBySession({
            itemId: sessionDetails.value.id,
            itemType: sessionDetails.value.itemType,
            updatePriceBySessionDto: { sessionAmounts: amountsToDto(this.uniformAmounts) },
            offset: this.attendeesPagination.offset,
            limit: this.attendeesPagination.limit,
          });
        } else {
          data = await apiInstance.updateCostConfigPriceByAttendee({
            itemId: sessionDetails.value.id,
            itemType: sessionDetails.value.itemType,
            updatePriceByAttendeeDto: {
              attendeeAmounts: this.attendees.map((a) => ({
                attendeeId: a.attendeeId,
                amounts: amountsToDto(a.amounts),
              })),
            },
            offset: this.attendeesPagination.offset,
            limit: this.attendeesPagination.limit,
          });
        }

        this.applyCostConfigResponse(data);
        await this.refreshIndicators();
        this.snapshot = null;
        this.isEditMode = false;
        feedback.success("Vos modifications ont été sauvegardées", "small");
      } catch (error) {
        console.error("Erreur lors de l'enregistrement des coûts:", error);
        feedback.error(`Une erreur est survenue lors de l'enregistrement des coûts`, "small");
        throw error;
      } finally {
        this.isSaving = false;
      }
    },

    async saveDefaultPrice(amounts: FinancingAmounts) {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      if (!sessionDetails.value?.id) return;

      const feedback = useFeedback();
      this.isSavingDefaultPrice = true;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CostConfigApi);

        // Push the by_attendee switch (with current values) before saving the
        // template, otherwise the BE rejects/ignores the default price.
        await apiInstance.updateCostConfigPriceByAttendee({
          itemId: sessionDetails.value.id,
          itemType: sessionDetails.value.itemType,
          updatePriceByAttendeeDto: {
            attendeeAmounts: this.attendees.map((a) => ({
              attendeeId: a.attendeeId,
              amounts: amountsToDto(a.amounts),
            })),
          },
          offset: this.attendeesPagination.offset,
          limit: this.attendeesPagination.limit,
        });

        const data = await apiInstance.updateCostConfigDefaultPrice({
          itemId: sessionDetails.value.id,
          itemType: sessionDetails.value.itemType,
          updateDefaultAttendeePriceDto: { defaultAttendeePrice: amountsToDto(amounts) },
          offset: this.attendeesPagination.offset,
          limit: this.attendeesPagination.limit,
        });
        this.applyCostConfigResponse(data);
        await this.refreshIndicators();
      } catch (error) {
        console.error("Erreur lors de l'enregistrement du prix par défaut:", error);
        feedback.error(`Une erreur est survenue lors de l'enregistrement du prix par défaut`, "small");
        throw error;
      } finally {
        this.isSavingDefaultPrice = false;
      }
    },

    async applyDefaultPrice() {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      if (!sessionDetails.value?.id) return;

      const feedback = useFeedback();
      this.isApplyingDefaultPrice = true;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CostConfigApi);
        const data = await apiInstance.applyCostConfigDefaultPrice({
          itemId: sessionDetails.value.id,
          itemType: sessionDetails.value.itemType,
          offset: this.attendeesPagination.offset,
          limit: this.attendeesPagination.limit,
        });
        this.applyCostConfigResponse(data);
        await this.refreshIndicators();
      } catch (error) {
        console.error("Erreur lors de l'application du prix par défaut:", error);
        feedback.error(`Une erreur est survenue lors de l'application du prix par défaut`, "small");
        throw error;
      } finally {
        this.isApplyingDefaultPrice = false;
      }
    },

    exitEditModeAfterDefaultPriceFlow() {
      this.snapshot = null;
      this.isEditMode = false;
    },

    setDistribution(nextDistribution: DistributionType) {
      if (nextDistribution === this.config.distribution) return;
      this.config.distribution = nextDistribution;

      if (nextDistribution === DistributionType.Session) {
        // Participant -> Session: pull the per-attendee amounts into the session row, but ONLY when
        // the session side is still unconfigured — never overwrite amounts the user/back already set.
        // Columns come from the backend (enabledFinancingModes), so there's nothing to seed here.
        if (isAmountsEmpty(this.uniformAmounts)) this.aggregateAttendeesIntoSession();
      } else if (this.attendees.every((a) => isAmountsEmpty(a.amounts))) {
        // Session -> Participant: even-split the session amount across attendees, but ONLY when no
        // per-attendee amount exists yet — preserves manual differentiation across round-trips.
        this.splitSessionAcrossAttendees();
      }
    },

    /** Participant -> Session: set the session row to the full-population per-mode total. */
    aggregateAttendeesIntoSession() {
      // Without an edit snapshot there's no per-page delta to layer — the server total IS the answer.
      if (!this.snapshot) {
        this.uniformAmounts = cloneAmounts(this.indicators.pedagogicalBreakdown);
        return;
      }
      const live = sumRowsByMode(this.attendees);
      const base = sumRowsByMode(this.snapshot.attendees);
      this.uniformAmounts = layeredAttendeeTotal(this.indicators.pedagogicalBreakdown, live, base);
    },

    /** Session -> Participant: even-split each session amount across all participants. */
    splitSessionAcrossAttendees() {
      const total = this.attendeesPagination.total || this.attendees.length;
      if (total <= 0) return;
      const { formatTo2Decimals } = useFormats();
      const split = Object.fromEntries(
        Object.entries(this.uniformAmounts).map(([id, value]) => [id, formatTo2Decimals(value / total)]),
      );
      this.attendees.forEach((a) => {
        a.amounts = { ...a.amounts, ...split };
      });
    },

    updateUniformAmount(financingModeId: string, value: number) {
      this.uniformAmounts[financingModeId] = Number.isFinite(value) ? value : 0;
    },

    updateAttendeeAmount(attendeeId: string, financingModeId: string, value: number) {
      const row = this.attendees.find((a) => a.attendeeId === attendeeId);
      if (!row) return;
      row.amounts[financingModeId] = Number.isFinite(value) ? value : 0;
    },

    // --- Manage financing modes on the session (PRD US-SESSION-02) ---

    /** Append a mode column to the right (no reordering); the column starts empty. */
    addSessionMode(mode: ISessionFinancingMode) {
      if (this.sessionModes.some((m) => m.financingModeId === mode.financingModeId)) return;
      if (this.sessionModes.length >= MAX_SESSION_FINANCING_MODES) return;
      this.sessionModes.push({ ...mode });
      // Don't clobber a maintained amount: only initialise the column to 0 when it has none yet.
      this.uniformAmounts[mode.financingModeId] ??= 0;
      this.attendees.forEach((a) => {
        a.amounts[mode.financingModeId] ??= 0;
      });
      if (this.defaultAttendeePrice) this.defaultAttendeePrice[mode.financingModeId] ??= 0;
    },

    /** Remove a mode column and its amounts. BUDGET_ENTREPRISE can't be removed. */
    removeSessionMode(financingModeId: string) {
      const mode = this.sessionModes.find((m) => m.financingModeId === financingModeId);
      if (!mode || mode.isSystem) return;
      this.sessionModes = this.sessionModes.filter((m) => m.financingModeId !== financingModeId);
      delete this.uniformAmounts[financingModeId];
      this.attendees.forEach((a) => {
        delete a.amounts[financingModeId];
      });
      if (this.defaultAttendeePrice) delete this.defaultAttendeePrice[financingModeId];
    },

    /** Whether a mode column holds any non-zero amount (drives the remove confirmation). */
    hasAmountsForMode(financingModeId: string): boolean {
      if ((this.uniformAmounts[financingModeId] ?? 0) > 0) return true;
      if (this.defaultAttendeePrice && (this.defaultAttendeePrice[financingModeId] ?? 0) > 0) return true;
      return this.attendees.some((a) => (a.amounts[financingModeId] ?? 0) > 0);
    },
  },
});
